"use client";

import { useSafeRequestSwr } from "@osn/auth";
import { AccountService, ApiRequestOptions } from "osn-participantapi-client";
import { useMyAccount } from "../my-account/hooks";
import { useTenantSettings } from "../tenant/hooks";
import { needToSignAgreement, needToSignCoachAgreement } from "./helpers";
import React from "react";
import { useRouter } from "next/navigation";

export function useMyAccountNotifications() {
  const callback = async (options: ApiRequestOptions) => await AccountService.getMyAccountNotifications(options);
  const key = () => ["AccountService.getMyAccountNotifications"];
  return useSafeRequestSwr(callback, key);
}

export function useMyAccountVolunteerRequests(enabled: boolean = true) {
  const callback = async (options: ApiRequestOptions) => await AccountService.getMyVolunteerRequests(options, false);
  const key = enabled ? ["AccountService.getMyVolunteerRequests", false] : null;
  return useSafeRequestSwr(callback, key);
}

export function useMyVolunteerPendingRequests() {
  const callback = async (options: ApiRequestOptions) => await AccountService.getMyVolunteerRequests(options, true);
  const key = () => ["AccountService.getMyVolunteerRequests", true];
  return useSafeRequestSwr(callback, key);
}

export function useAccountPhoto(id: number | undefined) {
  const callback = async (options: ApiRequestOptions) => await AccountService.getAccountPhoto(options, id!);
  const key = id ? ["AccountService.getAccountPhoto", id.toString()] : null;
  return useSafeRequestSwr(callback, key);
}

export function useVolunteerAgreementStatus() {
  const { data: myAccount } = useMyAccount();
  const { data: tenantSettings } = useTenantSettings();

  if (!myAccount || !tenantSettings) {
    return { needsToSign: false, isLoading: true };
  }

  return { needsToSign: needToSignCoachAgreement(myAccount, tenantSettings), isLoading: false };
}

export function useGeneralAgreementStatus() {
  const { data: myAccount } = useMyAccount();
  const { data: tenantSettings } = useTenantSettings();

  if (!myAccount || !tenantSettings) {
    return { needsToSign: false, isLoading: true };
  }

  return { needsToSign: needToSignAgreement(myAccount, tenantSettings), isLoading: false };
}

export function useProtectPageWithVolunteerAgreement(callbackUrl: string) {
  const { needsToSign, isLoading } = useVolunteerAgreementStatus();
  const { push } = useRouter();
  React.useEffect(() => {
    if (!isLoading && needsToSign) {
      push(`/coach-enrollment?callback=${callbackUrl}`);
    }
  }, [needsToSign, isLoading, push, callbackUrl]);

  return { isLoading, needsToSign };
}
